import { Fragment, useState } from 'react';
import Uppy from '@uppy/core';
import thumbnailGenerator from '@uppy/thumbnail-generator';
import { DragDrop } from '@uppy/react';
import 'uppy/dist/uppy.css';
import PropsTypes from 'prop-types';
import { Spinner, UncontrolledTooltip } from 'reactstrap';
import { showToastError } from '../../../@core/components/toastify/ToastTypes';
import { getThemeColor } from 'store/settings';
import { useSelector } from 'react-redux';
import styled from '@emotion/styled';
import {
  DEFAULT_IMAGE,
  LIMIT_IMAGE_FILE_UPLOAD,
  ONEKILOBYTE,
} from 'constants/constants';
import bytes from 'bytes';
import { MdOutlineCloudUpload } from 'react-icons/md';

const formatType = [
  'mp4',
  'm4a',
  'webm',
  'ogg',
  'ogv',
  'avi',
  'mpeg',
  'mpg',
  'mov',
  'wmv',
  '3gp',
  'flv',

  'mp3',
  'aac',
  'wav',
  'wave',
  'wma',
  'ra',
  'aif',
  'aiff',

  'gif',
  'jpg',
  'jpeg',
  'png',
  'pdf',
  'ppt',
  'pptx',
  'doc',
  'docx',
  'xls',
  'xlsx',
];

const FileUploaderBasic = (props) => {
  const [img, setImg] = useState(null);
  const {
    hideAllowfileName = true,
    allowedFileTypes,
    onChange,
    className,
    showPreview,
    disabled,
    maxSize,
    allowMultipleUploads,
    maxNumberOfFiles,
  } = props;

  //Color Theme
  const themeStatus = useSelector(getThemeColor);
  const metaData = useSelector((state) => state.settingAccount.metaData);
  const Stylescal = styled.div`
    .uppy-Root .uppy-DragDrop-browse {
      color: ${themeStatus};
    }
  `;
  const uppy = Uppy({
    meta: { type: 'avatar' },
    restrictions: {
      maxNumberOfFiles: maxNumberOfFiles || 1,
    },
    autoProceed: true,
    allowMultipleUploads: allowMultipleUploads,
  });

  uppy.use(thumbnailGenerator);

  uppy.on('file-added', (file) => {
    if (!allowMultipleUploads) {
      if (
        Array.isArray(allowedFileTypes) &&
        !allowedFileTypes.includes(file?.extension)
      ) {
        showToastError(
          `Unsupported file format  ${file?.data?.name}. Please use supported formats.`,
        );
        uppy.reset();
        return;
      } else {
        if (
          !file?.data?.name?.match(
            /\.(ppt|pptx|doc|docx|xls|xlsx|pdf|gif|jpg|jpeg|png|zip|mp4|m4a|webm|ogg|ogv|avi|mpeg|mpg|mov|wmv|3gp|flv|mp3|aac|ogg|wav|mpeg|webm|wave|wma|ra|aif|aiff)$/,
          )
        ) {
          showToastError(
            `Unsupported file format  ${file?.data?.name}. Please use supported formats.`,
          );
          uppy.reset();
          return;
        }
      }

      if (maxSize && file?.data?.size / (1024 * 1024) > maxSize) {
        showToastError(
          `File size exceeds the limit. Maximum allowed size is ${maxSize}MB`,
        );
        uppy.reset();
        return;
      }
      onChange(file);
    }
  });
  uppy.on('files-added', (files) => {
    if (allowMultipleUploads) {
      if (maxSize) {
        files?.map((file) => {
          if (maxSize && file?.data?.size / (1024 * 1024) > maxSize) {
            showToastError(
              `File size exceeds the limit. Maximum allowed size is ${maxSize}MB`,
            );
          }
        });
        files = files.filter(
          (file) => file?.data?.size / (1024 * 1024) <= maxSize,
        );
      }
      onChange(files);
    }
  });

  uppy.on('error', (error) => {
    console.error(error.stack);
  });

  uppy.on('thumbnail:generated', (file, preview) => {
    if (maxSize && file?.data?.size / (1024 * 1024) > maxSize) {
      return;
    }
    setImg(preview);
  });

  return (
    <>
      <div className={`file-uploader-custom ${className}`} id="inputDnD">
        <Stylescal>
          <DragDrop uppy={uppy} />
          <div className="customized-uppy-icon">
            <MdOutlineCloudUpload size={50} color={themeStatus} />
          </div>
        </Stylescal>
        {img !== null && showPreview ? (
          <img
            className="rounded course__thumbnail__reviews"
            src={img}
            alt="avatar"
          />
        ) : null}
        {disabled && (
          <div className="file-uploader-custom--disabled flexRow justify-content-center align-items-center">
            <Spinner style={{ color: `${themeStatus}` }} />
          </div>
        )}

        {/* {allowedFileTypes?.length ? (
        <UncontrolledTooltip placement="top" target="inputDnD">
          Accepted files{' '}
          {allowedFileTypes?.map((item, index) => {
            if (index !== allowedFileTypes.length - 1) {
              return item + ', ';
            }
            return item;
          })}
        </UncontrolledTooltip>
      ) : null} */}
      </div>
      {hideAllowfileName && (
        <div className="mt-1">
          <p className="home_banner_quote_name fron-weight-600 font-small-2  d-flex">
            <span className="text-nowrap">Supported Formats: &nbsp;</span>
            <span className="text-uppercase text-wrap">
              {allowedFileTypes?.map((type, index) =>
                (index ? ', ' : '').concat(type),
              )}
            </span>
          </p>
        </div>
      )}
    </>
  );
};

FileUploaderBasic.prototype = {
  className: PropsTypes.string,
  showPreview: PropsTypes.bool,
  allowedFileTypes: PropsTypes.array,
  onChange: PropsTypes.func,
  disabled: PropsTypes.func,
  maxSize: PropsTypes.number,
  maxNumberOfFiles: PropsTypes.number,
};

FileUploaderBasic.defaultProps = {
  allowedFileTypes: [
    'pdf',
    'ppt',
    'pptx',
    'doc',
    'docx',
    'xls',
    'xlsx', // Office documents

    'gif',
    'jpg',
    'jpeg',
    'png', // Images

    'zip', // ZIP archives

    'mp4',
    'm4a',
    'webm',
    'ogg',
    'ogv',
    'avi',
    'mpeg',
    'mpg',
    'mov',
    'wmv',
    '3gp',
    'flv', // Video formats

    'mp3',
    'aac',
    'ogg',
    'wav',
    'wave',
    'wma',
    'ra',
    'aif',
    'aiff', // Audio formats
  ],
};

export default FileUploaderBasic;
